import { FunctionComponent, useEffect, useState } from "react"
import { useTypedDispatch, useTypedSelector } from "../../../app/store"
import { setSelectedTreeItem } from "../decisionsSlice"
import { TreeItemNode } from "../types"
import { hasDescendant } from "../utils"

export type TreeProps = {
  choices: TreeItemNode[]
}

export type TreeItemProps = {
  treeNode: TreeItemNode
  level: number
  isSelected: boolean
  isExpanded: boolean
  selectedNode: number
  onSelectNode: (id: TreeItemNode | null) => void
  onSubmitNode: (childNode: TreeItemNode) => void
}

const Tree: FunctionComponent<TreeProps> = (props) => {
  const dispatch = useTypedDispatch()
  const [selectedNode, setSelectedNode] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedNodeObject, setSelectedNodeObject] =
    useState<TreeItemNode | null>(null)
  const [lastScrolledNode, setLastScrolledNode] = useState<Element>()
  const { treeSearchResults } = useTypedSelector(
    (state) => state.decisionsSlice,
  )

  const handleSelectNode = (choice: TreeItemNode | null) => {
    if (!choice) return
    setSelectedNode(choice?.contents.id ?? 0)
    dispatch(setSelectedTreeItem(choice.contents))
    setSelectedNodeObject(choice)
  }

  const handleSubmitChildNode = () => {
    setIsExpanded(false)
  }

  useEffect(() => {
    if (!selectedNodeObject) return
    dispatch(setSelectedTreeItem(selectedNodeObject.contents))
  }, [selectedNodeObject])

  // Check to see if we need to scroll the tree
  useEffect(() => {
    // Get first match in the tree (using .tree-search-match), and scroll to that
    // element.
    const firstMatch = document.querySelector(".tree-search-match")
    const overrideMatch = document.querySelector(".tree-search-match-override")
    if (firstMatch || overrideMatch) {
      const targetMatch = overrideMatch ?? firstMatch
      if (targetMatch == null) return
      if (targetMatch == lastScrolledNode) return
      targetMatch.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      })
      setLastScrolledNode(targetMatch)
    }
  }, [treeSearchResults])

  return (
    <div>
      {props.choices?.map((choice) => {
        return (
          <TreeItem
            treeNode={choice}
            level={0}
            key={choice.contents.id}
            isSelected={choice.contents.id === selectedNode}
            isExpanded={isExpanded}
            selectedNode={selectedNode}
            onSelectNode={handleSelectNode}
            onSubmitNode={handleSubmitChildNode}
          />
        )
      })}
    </div>
  )
}

const TreeItem: FunctionComponent<TreeItemProps> = (props) => {
  // Use consistent pixel-based indentation for proper visual hierarchy
  // Each level adds 20px of indentation for clear parent-child relationships
  const indentPx = props.level * 20

  const [isExpanded, setIsExpanded] = useState(props.treeNode.isExpanded)
  const [itemClasses, setItemClasses] = useState("")
  const [nodeLabel, setNodeLabel] = useState(<span></span>)

  const { treeSearchResults: searchResults, activeSearchQuery: searchQuery } =
    useTypedSelector((state) => state.decisionsSlice)

  const { decisions } = useTypedSelector((state) => state.simulationSlice)

  const handleClick = () => {
    if (!props.treeNode.isLeafNode) {
      toggleExpandedState()
    } else {
      props.onSelectNode(props.treeNode)
    }
  }

  const handleSubmitChildNode = () => {
    collapse()
  }

  const toggleExpandedState = () => {
    if (isExpanded) {
      collapse()
    } else {
      expand()
    }
  }

  const expand = () => {
    setIsExpanded(true)
    props.treeNode.isExpanded = true
  }

  const collapse = () => {
    setIsExpanded(false)
    props.treeNode.isExpanded = false
  }

  // Manage highlighting of selected nodes
  useEffect(() => {
    const isSelectedClasses = props.isSelected
      ? "character-primary-85 bg-blue-light-200 px-1.5 rounded-md"
      : ""
    let classes = "cursor-pointer flex-1 relative leading-[22px] py-0.5"
    classes += ` ${isSelectedClasses}`
    setItemClasses(classes)
  }, [props.isSelected])

  // As search results are generated, update the tree to reflect them
  useEffect(() => {
    if (!searchResults) return
    const isMatch = searchResults.includes(props.treeNode.contents.id)
    if (isMatch) {
      expand()
      setNodeLabel(highlightMatch(props.treeNode.contents.text, searchQuery))
    } else {
      setNodeLabel(<span>{props.treeNode.contents.text}</span>)
      if (hasDescendant(props.treeNode, searchResults)) {
        expand()
      } else {
        collapse()
      }
    }
  }, [searchResults])

  // When this item is submitted, deselect it and collapse its parent
  useEffect(() => {
    if (!props.isSelected) return
    // Get most recent choice
    const decision = decisions[decisions.length - 1]
    if (decision?.item?.id === props.treeNode.contents.id) {
      // This was the node just added to choices
      props.onSelectNode(null)
      props.onSubmitNode(props.treeNode)
    }
  }, [decisions])

  // Watch for changes to the node's expanded state, and update our local state
  useEffect(() => {
    setIsExpanded(props.treeNode.isExpanded)
  }, [props.treeNode.isExpanded])

  return (
    <div id={`tree-item-${props.treeNode.contents.id}-container`}>
      <div
        className="self-stretch flex flex-col items-start justify-start py-0"
        style={{ paddingLeft: `${indentPx}px` }}>
        <div className="rounded-sm bg-base-white overflow-hidden flex flex-row items-start justify-start">
          {(!props.treeNode.isLeafNode && (
            <img
              className="relative w-6 h-6 cursor-pointer"
              alt=""
              src={
                isExpanded
                  ? "/v3/assets/tree-switcher.svg"
                  : "/v3/assets/tree-switcher-closed.svg"
              }
              onClick={() => setIsExpanded(!isExpanded)}
            />
          )) || <div className="w-2 pt-1">–</div>}
          <div
            id={`tree-item-${props.treeNode.contents.id}`}
            className="flex-1 flex flex-row items-center justify-start py-0 px-1"
            onClick={handleClick}>
            <div className={itemClasses}>{nodeLabel}</div>
          </div>
        </div>
      </div>
      <div className={(isExpanded ? "block" : "hidden") + " self-stretch"}>
        {props.treeNode.children?.map((child) => {
          return (
            <TreeItem
              treeNode={child}
              level={props.level + 1}
              key={child.contents.id}
              isSelected={child.contents.id === props.selectedNode}
              isExpanded={false}
              selectedNode={props.selectedNode}
              onSelectNode={props.onSelectNode}
              onSubmitNode={handleSubmitChildNode}
            />
          )
        })}
      </div>
    </div>
  )
}

const highlightMatch = (text: string, query: string): JSX.Element => {
  if (!query || query.length < 3) return <span>{text}</span>
  const index = text.toLowerCase().indexOf(query.toLowerCase())
  if (index !== -1) {
    return (
      <span className="tree-search-match">
        {text.substring(0, index)}
        <strong>{text.substring(index, index + query.length)}</strong>
        {text.substring(index + query.length)}
      </span>
    )
  }
  // We only end up here when we have a match but cannot find the text of the query
  // This could happen because we allow hits on synonyms, but the synonyms are not
  // present in the text. In this case, we just highlight the whole text.
  return (
    <span>
      <strong>{text}</strong>
    </span>
  )
}

export default Tree
